import React, { useCallback, useRef, useState } from 'react';
import { SpinnerGap } from '@phosphor-icons/react';
import { submitSectorReviewFeedback } from '@/lib/api';
import { INTENT_TO_BUTTON, COCKPIT_LABELS } from '@/config/task-display-config';
import StatusBox from '@/components/shared/StatusBox';
import {
    DS_BORDERS,
    DS_COLORS,
    DS_SPACING,
    DS_TYPOGRAPHY,
    DESIGN_INTENTS,
} from '@/config/ui-invariants';
import { toast } from 'sonner';

/**
 * StartupReviewDetail — Inbox detail renderer for work_type=startup_review.
 *
 * #923 (AGT-021 SIA Junior) queues a high-confidence sector-label
 * classification as a CEO-review card instead of writing it straight onto
 * Collections.STARTUPS: candidates are stored on
 * startup.pending_sector_review (ceo_decision=pending) and this card points
 * at them. Accept no longer writes a sector (R7, 15 Oct 2026: research sets it through
 * the one door; the suggestion stays on the card); reject discards the suggestion and
 * lets SIA try again on the next enrichment pass.
 *
 * Payload shape (from item.payload, built by
 * backend/routes/work_inbox.py::build_work_item_from_contract's
 * STARTUP_REVIEW bypass branch):
 *   linked_startup_id: string  — ID passed to POST /intelligence/sector-review/{id}/feedback
 *   startup_name: string
 *   fields_pending: string[]   — field names (always present; from the card itself)
 *   candidates: [{ field, value, confidence, source, sector_l1?, sector_l2? }]
 *               — live values merged in from Collections.STARTUPS at read time;
 *                 may be empty if the underlying record already resolved
 *                 (e.g. a concurrent accept/reject) — degrades to fields_pending only.
 *
 * No route writes these canonical taxonomy fields from this card any more
 * (Law §8.2) — this card is deliberately off the
 * generic inbox accept/reject/defer waterfall (item.actions === ['view']).
 *
 * Accessibility (the CEO is a screen-reader user, not just low-vision — a
 * visual-only implementation does not count as working):
 *   - The card is a `role="region"` labelled by its own heading (h2), not by
 *     a bare aria-label duplicating the visible title.
 *   - Candidates render as a real `<ul>`/`<li>` list, not styled divs.
 *   - A `StatusBox` (shared component, `role="status"` + `aria-live="polite"`)
 *     carries the pending-count / success / error message — the toast is a
 *     visual echo only, never the sole channel; a screen reader announces the
 *     StatusBox text on every change without the user needing to find it.
 *   - Accept/Reject buttons have explicit accessible names naming the startup
 *     ("Accept sector review for Acme Robotics"), plus aria-busy/aria-disabled
 *     mirroring the busy state.
 *   - After an action resolves, focus moves to the status region (via a
 *     tabIndex={-1} wrapper) BEFORE onRefresh() fires, so the outcome is both
 *     announced and focus lands somewhere stable even if the parent's refresh
 *     removes this card from the list on the next render.
 *
 * Authority: WI-INFRA-UPGRADE-CLOSEOUT-BUNDLE-001 Leg D; #923
 * (WI-FREEZE-EXIT-SHORTLIST-REPAIRS-002 Leg 2).
 */
export function StartupReviewDetail({ item, onRefresh }) {
    const [busy, setBusy] = useState(null); // 'accept' | 'reject' | null

    const payload = item?.payload || {};
    const startupId = payload.linked_startup_id;
    const startupName = payload.startup_name || item?.title || 'Untitled Startup';
    const candidates = Array.isArray(payload.candidates) ? payload.candidates : [];
    const fieldsPending = Array.isArray(payload.fields_pending) ? payload.fields_pending : [];
    const fieldLabels = COCKPIT_LABELS.startupReviewFieldLabels || {};
    const hasLiveCandidates = candidates.length > 0;
    const pendingCount = hasLiveCandidates ? candidates.length : fieldsPending.length;

    const pendingMessage = `${pendingCount} sector-label ${pendingCount === 1 ? 'field is' : 'fields are'} pending your review.`;
    const [statusMessage, setStatusMessage] = useState(pendingMessage);
    const [statusIntent, setStatusIntent] = useState('info');
    const statusRegionRef = useRef(null);

    const handleFeedback = useCallback(async (action) => {
        if (!startupId) {
            const msg = 'Startup ID not found — cannot save.';
            setStatusMessage(msg);
            setStatusIntent('negative');
            toast.error(msg);
            statusRegionRef.current?.focus();
            return;
        }
        setBusy(action);
        try {
            const result = await submitSectorReviewFeedback(startupId, action);
            if (action === 'accept' && result && result.status === 'not_applied') {
                // R7 (15 Oct 2026): Accept no longer writes a sector; the suggestion stays on the card. Say so, never "saved".
                const keptMsg = `Nothing was saved for ${startupName}. The sector is set by research; the suggestion stays on this card.`;
                setStatusMessage(keptMsg);
                setStatusIntent('info');
                statusRegionRef.current?.focus();
                return;
            }
            const successMsg = action === 'accept'
                ? `Sector labels saved for ${startupName}.`
                : `Sector labels rejected for ${startupName}.`;
            setStatusMessage(successMsg);
            setStatusIntent('positive');
            toast.success(successMsg);
            // Move focus to the status announcement BEFORE the parent's refresh
            // potentially removes this card from the list — the screen-reader
            // user hears the outcome and lands somewhere stable rather than
            // being stranded on a control that may disappear on the next render.
            statusRegionRef.current?.focus();
            onRefresh && onRefresh();
        } catch (e) {
            const errMsg = 'Could not save — please try again.';
            setStatusMessage(errMsg);
            setStatusIntent('negative');
            toast.error(errMsg);
            statusRegionRef.current?.focus();
        } finally {
            setBusy(null);
        }
    }, [startupId, startupName, onRefresh]);

    return (
        <div
            role="region"
            aria-labelledby="startup-review-heading"
            style={{ fontFamily: DS_TYPOGRAPHY.bodyMd.fontFamily }}
        >
            {/* Title — the region's accessible name comes from this heading via aria-labelledby */}
            <h2
                id="startup-review-heading"
                style={{
                    fontFamily: DS_TYPOGRAPHY.listItemTitle.fontFamily,
                    fontSize: DS_TYPOGRAPHY.listItemTitle.fontSize,
                    fontWeight: DS_TYPOGRAPHY.listItemTitle.fontWeight,
                    color: DS_COLORS.text.primary,
                    marginBottom: '4px',
                    marginTop: 0,
                }}
            >
                {startupName}
            </h2>

            {/* Announced status — role="status" + aria-live="polite" (StatusBox).
                Carries the pending count on load and the accept/reject outcome
                after an action; focus lands here (tabIndex=-1) so the message is
                both announced AND reachable, not just visually present. */}
            <div ref={statusRegionRef} tabIndex={-1} className="mb-5 mt-3 outline-none">
                <StatusBox intent={statusIntent} label={statusMessage} />
            </div>

            {/* Candidate list — a real list, not styled divs */}
            {hasLiveCandidates ? (
                <ul className="flex flex-col gap-3 mb-5 list-none p-0 m-0" data-testid="startup-review-candidates">
                    {candidates.map((cand) => (
                        <li key={cand.field}>
                            <CandidateRow
                                field={cand.field}
                                label={fieldLabels[cand.field] || cand.field}
                                value={cand.value}
                                confidence={cand.confidence}
                                sectorL1={cand.sector_l1}
                                sectorL2={cand.sector_l2}
                            />
                        </li>
                    ))}
                </ul>
            ) : (
                <div className="mb-5" data-testid="startup-review-fields-only">
                    <p
                        style={{
                            ...DS_TYPOGRAPHY.bodyMd,
                            color: DS_COLORS.text.muted,
                            fontStyle: 'italic',
                            marginBottom: '8px',
                        }}
                    >
                        Field detail is unavailable — the underlying record may have changed.
                        Pending fields:
                    </p>
                    <ul className="flex gap-2 flex-wrap list-none p-0 m-0">
                        {fieldsPending.map((field) => (
                            <li key={field}>
                                <span
                                    className={`inline-flex items-center px-3 py-1 rounded-full border ${DESIGN_INTENTS.neutral.bg} ${DESIGN_INTENTS.neutral.text} ${DESIGN_INTENTS.neutral.border}`}
                                    style={{
                                        fontFamily: DS_TYPOGRAPHY.sectionLabel.fontFamily,
                                        fontSize: '12px',
                                        fontWeight: 700,
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.03em',
                                    }}
                                >
                                    {fieldLabels[field] || field}
                                </span>
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            {/* Action bar */}
            <div
                className="flex items-center gap-3 mt-6 pt-4 flex-wrap"
                style={{ borderTop: `${DS_BORDERS.width} solid ${DS_COLORS.border.default}` }}
            >
                <button
                    type="button"
                    onClick={() => handleFeedback('reject')}
                    disabled={!!busy || !startupId}
                    aria-disabled={!!busy || !startupId}
                    aria-busy={busy === 'reject'}
                    aria-label={`Reject sector review for ${startupName}`}
                    className={`inline-flex items-center gap-2 px-4 rounded-md border ${INTENT_TO_BUTTON.negative.bg} ${INTENT_TO_BUTTON.negative.text} ${INTENT_TO_BUTTON.negative.border} ${INTENT_TO_BUTTON.negative.hover} disabled:opacity-50`}
                    style={{
                        minHeight: DS_SPACING.minTouchTarget + 'px',
                        borderRadius: DS_BORDERS.radiusButton,
                        fontFamily: DS_TYPOGRAPHY.actionBtn.fontFamily,
                        fontSize: DS_TYPOGRAPHY.actionBtn.fontSize,
                        fontWeight: DS_TYPOGRAPHY.actionBtn.fontWeight,
                    }}
                >
                    {busy === 'reject' ? <SpinnerGap aria-hidden="true" className="w-4 h-4 animate-spin" /> : null}
                    Reject
                </button>
                <div className="flex-1" />
                <button
                    type="button"
                    onClick={() => handleFeedback('accept')}
                    disabled={!!busy || !startupId}
                    aria-disabled={!!busy || !startupId}
                    aria-busy={busy === 'accept'}
                    aria-label={`Accept sector review for ${startupName}`}
                    className={`inline-flex items-center gap-2 px-5 rounded-md border ${INTENT_TO_BUTTON.primary.bg} ${INTENT_TO_BUTTON.primary.text} ${INTENT_TO_BUTTON.primary.border} ${INTENT_TO_BUTTON.primary.hover} disabled:opacity-50`}
                    style={{
                        minHeight: DS_SPACING.minTouchTarget + 'px',
                        borderRadius: DS_BORDERS.radiusButton,
                        fontFamily: DS_TYPOGRAPHY.actionBtn.fontFamily,
                        fontSize: DS_TYPOGRAPHY.actionBtn.fontSize,
                        fontWeight: DS_TYPOGRAPHY.actionBtn.fontWeight,
                    }}
                >
                    {busy === 'accept' ? <SpinnerGap aria-hidden="true" className="w-4 h-4 animate-spin" /> : null}
                    Accept
                </button>
            </div>
        </div>
    );
}

// ─── CandidateRow helper ────────────────────────────────────────────────────────
function CandidateRow({ field, label, value, confidence, sectorL1, sectorL2 }) {
    const confidencePct = typeof confidence === 'number' ? Math.round(confidence * 100) : null;
    const intentStyle = DESIGN_INTENTS.info;
    return (
        <div
            data-testid={`startup-review-candidate-${field}`}
            className={`flex items-center justify-between rounded-lg border px-4 py-3 ${intentStyle.bg} ${intentStyle.border}`}
        >
            <div>
                <div
                    style={{
                        fontFamily: DS_TYPOGRAPHY.sectionLabel.fontFamily,
                        fontSize: DS_TYPOGRAPHY.sectionLabel.fontSize,
                        fontWeight: DS_TYPOGRAPHY.sectionLabel.fontWeight,
                        textTransform: 'uppercase',
                        letterSpacing: '0.08em',
                        color: DS_COLORS.text.secondary,
                        marginBottom: '2px',
                    }}
                >
                    {label}
                </div>
                <div
                    style={{
                        fontFamily: DS_TYPOGRAPHY.bodyMd.fontFamily,
                        fontSize: DS_TYPOGRAPHY.bodyMd.fontSize,
                        color: DS_COLORS.text.primary,
                        fontWeight: 600,
                    }}
                >
                    {value}
                    {(sectorL1 || sectorL2) && (
                        <span
                            style={{
                                ...DS_TYPOGRAPHY.bodySm,
                                color: DS_COLORS.text.secondary,
                                fontWeight: 400,
                                marginLeft: '8px',
                            }}
                        >
                            ({[sectorL1, sectorL2].filter(Boolean).join(' → ')})
                        </span>
                    )}
                </div>
            </div>
            {confidencePct !== null && (
                <span
                    className={`inline-flex items-center px-3 py-1 rounded-full border ${intentStyle.bg} ${intentStyle.text} ${intentStyle.border}`}
                    style={{
                        fontFamily: DS_TYPOGRAPHY.sectionLabel.fontFamily,
                        fontSize: '12px',
                        fontWeight: 700,
                    }}
                >
                    {confidencePct}% confidence
                </span>
            )}
        </div>
    );
}
